import { motion } from "framer-motion";
import { ExternalLink, GitBranch, ShieldCheck } from "lucide-react";
import { MiniGraph } from "./MiniGraph";
import { useI18n } from "../i18n/LanguageContext";

const ReliabilityPill = ({ score }) => {
  const tone =
    score >= 95 ? "#4BE3FF" : score >= 88 ? "#1E6FFF" : "#A7B4C2";
  return (
    <div className="flex items-center gap-2">
      <ShieldCheck className="w-4 h-4" style={{ color: tone }} />
      <div className="flex items-center gap-2">
        <div className="w-24 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ background: tone }}
            initial={{ width: 0 }}
            whileInView={{ width: `${score}%` }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut" }}
          />
        </div>
        <span className="font-mono text-[13px]" style={{ color: tone }}>
          {score}%
        </span>
      </div>
    </div>
  );
};

export const CognitiveLinkCard = ({ link, index = 0 }) => {
  const { t } = useI18n();
  return (
    <motion.article
      data-testid={`cognitive-link-${link.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="group relative rounded-2xl border border-[#A7B4C2]/15 bg-[#0A1A2F] p-6 md:p-7 transition-all duration-500 hover:border-[#4BE3FF]/40 hover:-translate-y-1 hover:cyan-glow"
    >
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-display font-semibold text-lg md:text-xl text-white leading-snug group-hover:text-[#4BE3FF] transition-colors duration-300">
          {link.title}
        </h3>
        <span className="shrink-0 font-mono text-[11px] text-[#A7B4C2]/60 pt-1">
          {link.id.toUpperCase()}
        </span>
      </div>

      <div className="h-px w-full bg-gradient-to-r from-[#1E6FFF] to-transparent my-5" />

      {/* Summary */}
      <p className="font-ui text-[14px] text-[#A7B4C2] leading-relaxed">
        {link.summary}
      </p>

      {/* Concepts */}
      <div className="mt-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-[#A7B4C2]/50 mb-2">
          {t("card.keyConcepts")}
        </p>
        <div className="flex flex-wrap gap-2">
          {link.concepts.map((c) => (
            <span
              key={c}
              className="font-mono text-[11px] text-[#4BE3FF] border border-[#4BE3FF]/25 bg-[#4BE3FF]/5 rounded-full px-2.5 py-1"
            >
              {c}
            </span>
          ))}
        </div>
      </div>

      <div className="h-px w-full bg-white/5 my-5" />

      {/* Reliability + Relations */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#A7B4C2]/50 mb-2">
            {t("card.reliability")}
          </p>
          <ReliabilityPill score={link.reliability} />
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#A7B4C2]/50 mb-1">
            {t("card.relations")}
          </p>
          <MiniGraph nodes={link.graph.nodes} edges={link.graph.edges} />
        </div>
      </div>

      <div className="h-px w-full bg-white/5 my-5" />

      {/* Source + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="font-mono text-[12px] text-[#A7B4C2]/70 truncate">
          ↳ {link.source}
        </span>
        <div className="flex items-center gap-2">
          <button
            data-testid={`open-source-${link.id}`}
            className="flex items-center gap-1.5 rounded-lg border border-white/10 hover:border-[#4BE3FF]/50 hover:text-[#4BE3FF] text-[#A7B4C2] font-mono text-[12px] px-3 py-1.5 transition-colors duration-300"
          >
            <ExternalLink className="w-3.5 h-3.5" /> {t("card.openSource")}
          </button>
          <button
            data-testid={`explore-relations-${link.id}`}
            className="flex items-center gap-1.5 rounded-lg bg-[#1E6FFF] hover:bg-[#4BE3FF] hover:text-[#0A1A2F] text-white font-mono text-[12px] px-3 py-1.5 transition-colors duration-300"
          >
            <GitBranch className="w-3.5 h-3.5" /> {t("card.exploreRelations")}
          </button>
        </div>
      </div>
    </motion.article>
  );
};
